/**
 * usePortfolioScopeParams — convenience hook that turns the active
 * multi-portfolio selection + calendar-scope toggle into a ready
 * `params` object every consumer can spread directly into its axios
 * call.
 *
 * Round 92o++++++++N++++++++++ (Jun 2026).
 *
 * Returns:
 *   { portfolio_id?: string, include_watchlist?: boolean }
 *
 * - `portfolio_id` is omitted entirely when the user is on the "All
 *   Portfolios" view so the backend uses the legacy user-wide
 *   aggregation (free-tier behaviour preserved).
 * - `include_watchlist` is only included when the user has flipped
 *   the calendar-scope toggle to "portfolio only"; otherwise omitted
 *   so the backend default (True) kicks in.
 */
import { useSyncExternalStore } from 'react';
import { getSnapshot as getPortfolioSnap, subscribe as subPortfolio } from './portfolioStore';
import { getMode as getScope, subscribe as subScope } from './calendarScopeStore';

function _snap() {
  return {
    activeId: getPortfolioSnap().activeId || null,
    scope: getScope(),
  };
}

let cachedSnap = _snap();
let cachedKey = `${cachedSnap.activeId}|${cachedSnap.scope}`;

function _composedSnapshot() {
  const next = _snap();
  const key = `${next.activeId}|${next.scope}`;
  if (key !== cachedKey) {
    cachedKey = key;
    cachedSnap = next;
  }
  return cachedSnap;
}

function _composedSubscribe(fn) {
  const a = subPortfolio(fn);
  const b = subScope(fn);
  return () => { a(); b(); };
}

export function usePortfolioScopeParams({ withWatchlistFlag = false } = {}) {
  const { activeId, scope } = useSyncExternalStore(
    _composedSubscribe,
    _composedSnapshot,
    _composedSnapshot,
  );
  const params = {};
  if (activeId) params.portfolio_id = activeId;
  if (withWatchlistFlag && scope === 'portfolio') params.include_watchlist = false;
  return params;
}
